import React, { useEffect } from 'react';
import Reviews from '@/components/Reviews';
import { runStripeWebhookTest } from '@/utils/webhookTestUtils';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import logoOptimized from '@/assets/logo-optimized.webp';
import { Shield, Recycle, Award, MapPin } from 'lucide-react';

const Index = () => {
  
  useEffect(() => {
    // Register webhook test function if it's not already defined
    if (typeof window !== 'undefined' && !window.runStripeWebhookTest) {
      window.runStripeWebhookTest = runStripeWebhookTest;
    }
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Hero Section */}
      <header className="bg-green-700 text-white py-8 px-4 text-center">
        <div className="mb-4">
          <img 
            src={logoOptimized} 
            alt="Bins and Things Logo - Bin There, Cleaned That!" 
            className="w-32 h-32 md:w-40 md:h-40 mx-auto object-contain"
            loading="eager"
          />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold mb-2">Eco-Friendly Bin Washing Dunblane & Stirling</h1>
        <p className="text-lg md:text-xl opacity-90">Professional SEPA-Compliant Bin Cleaning Service</p>
        
        
        {/* No Contract Highlight */}
        <div className="mb-6">
          <p className="text-yellow-200 font-semibold text-lg">
            ✅ No contract • Cancel anytime
          </p>
        </div>
        
        {/* Enhanced Compliance & Trust Badges */}
        <div className="mt-8 mb-6">
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <div className="bg-blue-100 text-blue-800 px-4 py-3 rounded-lg font-semibold text-sm flex items-center gap-2 border border-blue-200">
              <Shield className="w-5 h-5" />
              SEPA Compliant
            </div>
            <div className="bg-green-100 text-green-800 px-4 py-3 rounded-lg font-semibold text-sm flex items-center gap-2 border border-green-200">
              <Recycle className="w-5 h-5" />
              Eco-Friendly
            </div>
            <div className="bg-purple-100 text-purple-800 px-4 py-3 rounded-lg font-semibold text-sm flex items-center gap-2 border border-purple-200">
              <Award className="w-5 h-5" />
              Locally Owned
            </div>
            <div className="bg-orange-100 text-orange-800 px-4 py-3 rounded-lg font-semibold text-sm flex items-center gap-2 border border-orange-200">
              <MapPin className="w-5 h-5" />
              100% Automated
            </div>
          </div>
        </div>
      </header>

      {/* Why Trust Us Section */}
      <section className="max-w-4xl mx-auto my-8 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg shadow-lg p-6 md:p-8 mx-4 border border-green-200">
        <h2 className="text-2xl md:text-3xl font-bold text-green-700 text-center mb-8">
          Why Trust Bins and Things?
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8" />
            </div>
            <h3 className="font-bold text-blue-800 mb-3">SEPA Compliance</h3>
            <ul className="text-sm text-gray-700 space-y-2 text-left">
              <li>• Fully licensed waste water disposal</li>
              <li>• Regulated environmental practices</li>
              <li>• Legal compliance guaranteed</li>
              <li>• No DIY mess or illegal dumping</li>
            </ul>
          </div>
          
          <div className="text-center">
            <div className="bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Recycle className="w-8 h-8" />
            </div>
            <h3 className="font-bold text-green-800 mb-3">Eco-Friendly Process</h3>
            <ul className="text-sm text-gray-700 space-y-2 text-left">
              <li>• Water recycling system</li>
              <li>• Biodegradable cleaning products</li>
              <li>• Minimal environmental impact</li>
              <li>• Sustainable business practices</li>
            </ul>
          </div>
          
          <div className="text-center">
            <div className="bg-purple-600 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Award className="w-8 h-8" />
            </div>
            <h3 className="font-bold text-purple-800 mb-3">Local & Trusted</h3>
            <ul className="text-sm text-gray-700 space-y-2 text-left">
              <li>• Family-owned Dunblane business</li>
              <li>• Supporting local community</li>
              <li>• Personal service guarantee</li>
              <li>• Transparent, honest pricing</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-8 p-4 bg-white rounded-lg border border-green-300">
          <p className="text-center text-sm text-gray-700">
            <span className="font-semibold text-green-700">Professional & Insured:</span> We're a legitimate business with proper insurance, 
            environmental compliance, and a commitment to excellence. No cowboys, no corner-cutting – just professional bin cleaning you can trust.
          </p>
        </div>
      </section>

      {/* Main CTA Section */}
      <section className="max-w-2xl mx-auto my-8 bg-white rounded-lg shadow-md p-6 md:p-8 mx-4">
        <h2 className="text-2xl md:text-3xl font-bold text-green-700 text-center mb-4">
          Eco-Friendly Bin Washing Service in Dunblane & Stirling
        </h2>
        <div className="text-center mb-6">
          <p className="text-gray-700 mb-4 leading-relaxed">
            We're a small business based in <span className="font-semibold text-green-600">Dunblane</span>, 
            providing professional bin washing services that make your bins (and neighbourhood) sparkle—safely and sustainably.
          </p>
          <p className="text-green-600 font-semibold mb-4">
            Professional bin cleaning serving Dunblane, Bridge of Allan, and Doune in the Stirling area.
          </p>
          
          {/* Automated Service Highlight */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
            <h3 className="font-semibold text-blue-800 mb-2">🤖 It's That Easy!</h3>
            <p className="text-sm text-blue-700 mb-2">
              Just give us your address and we handle everything else:
            </p>
            <ul className="text-xs text-blue-600 space-y-1">
              <li>✓ We automatically find your bin collection day</li>
              <li>✓ We track any council changes to your schedule</li>
              <li>✓ We show up on the right day, every time</li>
              <li>✓ You do absolutely nothing except enjoy clean bins!</li>
            </ul>
          </div>
        </div>
        
        <div className="text-center">
          <Button asChild variant="default" size="lg" className="text-lg px-8 py-3 mb-3 bg-blue-600 hover:bg-blue-700 text-white">
            <Link to="/guest-checkout">Quick Sign Up - Get Started!</Link>
          </Button>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-green-600">
              ✅ No contract • Cancel anytime
            </p>
            <p className="text-sm text-gray-600">
              Hassle-free • 100% satisfaction guarantee
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="max-w-3xl mx-auto my-8 bg-white rounded-lg shadow-md p-6 md:p-8 mx-4">
        <h2 className="text-2xl font-bold text-green-700 mb-6 text-center">
          Why Choose Our Eco-Friendly Bin Washing Service?
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-green-600 mb-2">Locally Owned</h3>
            <p className="text-gray-700 text-sm">
              Support a small business that cares about our community.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-green-600 mb-2">SEPA-Compliant</h3>
            <p className="text-gray-700 text-sm">
              We're fully compliant and eco-friendly—no DIY mess or illegal waste!
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-green-600 mb-2">Fully Automated</h3>
            <p className="text-gray-700 text-sm">
              Our smart system tracks your bin days automatically. Even if the council changes schedules, we stay on top of it!
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-green-600 mb-2">Eco-Friendly</h3>
            <p className="text-gray-700 text-sm">
              We recycle water and use safe detergents. Good for your bins, great for the planet!
            </p>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="max-w-3xl mx-auto my-8 bg-white rounded-lg shadow-md p-6 md:p-8 mx-4">
        <h2 className="text-2xl font-bold text-green-700 mb-4 text-center">
          What Your Neighbours Say
        </h2>
        <Reviews />
      </section>

      {/* Contact Section */}
      <section className="max-w-3xl mx-auto my-8 bg-white rounded-lg shadow-md p-6 md:p-8 mx-4">
        <h2 className="text-2xl font-bold text-green-700 mb-4 text-center">
          Questions? Get in Touch!
        </h2>
        <div className="text-center">
          <p className="text-gray-700 mb-4">
            Email: <a href="mailto:admin@binsandthings.co.uk" className="text-green-600 hover:text-green-700 font-semibold">
              admin@binsandthings.co.uk
            </a>
          </p>
          <Button asChild variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
            <Link to="/contact">Or use our quick contact form</Link>
          </Button>
          
          {/* Social Media Section */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-green-700 mb-3">Follow Us on Social Media</h3>
            <div className="flex justify-center gap-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61571056657200" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </a>
              <a 
                href="https://www.instagram.com/bins.andthings" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                @bins.andthings
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center py-6 px-4 text-gray-600 mt-8">
        <p className="text-sm mb-2">
          © 2025 Bins and Things | Proudly serving Dunblane, Bridge of Allan, and Doune.
        </p>
        <p className="text-xs">
          SEPA-Compliant | Eco-Friendly | Small Business, Big Heart
        </p>
      </footer>
    </div>
  );
};

export default Index;
